import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { ReactComponent as CopyIcon } from '@assets/svg/ic-copyDetails.svg';

import ButtonCopyDetails from '@components/common/ButtonCopyDetails/ButtonCopyDetails';
import Gallery from '@components/common/Gallery/Gallery';
import InfoField from '@components/common/InfoField/InfoField';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IInstallationPoint } from '@model/installationPoint';

import { useInstallationPointPhotosQuery } from '@services/useInstallationPointPhotosQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { getDetailsData } from '../../helpers/getDetailsData';
import SensorType from '../SensorType/SensorType';
import SensorInformationSkeleton from './SensorInformationSkeleton';
import style from './style.module.scss';

interface IProps {
  point?: IInstallationPoint;
  sensorHEX?: string;
  isLoading?: boolean;
}

const SensorInformation: React.FC<IProps> = ({ point, sensorHEX = '', isLoading = false }) => {
  const { t } = useTranslation();

  const { data: profileData } = useUserProfileQuery();
  const { customerId, pointId } = useParams();
  const { pathname } = useLocation();

  const { setActiveModal } = useModalActions();

  const { data: imagesData } = useInstallationPointPhotosQuery({
    installationPointId: pointId,
    enabled: !!pointId,
  });

  const equipment = point?.equipment;
  const hardware_type = point?.sensor?.hardwareType || point?.pointType;
  const copyDetailsData = getDetailsData(t, point, equipment);
  const locationInfo = point?.equipment?.location;
  const isSensor = hardware_type === 'sensor';
  const isMote = hardware_type === 'mote';

  const handleOpenEdit = () => {
    setActiveModal({
      name: MODAL_NAMES.editPhotosModal,
      options: {
        photos: imagesData,
        equipmentType: hardware_type,
        equipmentId: equipment?.id,
        itemId: pointId,
      },
    });
  };

  const copyHexSerial = () => {
    try {
      navigator.clipboard.writeText(sensorHEX).then(() => {
        showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
      });
    } catch (e) {
      console.log(e);
    }
  };

  if (isLoading) {
    return <SensorInformationSkeleton />;
  }

  if (!point) {
    return null;
  }

  return (
    <div className={style.sensor}>
      <div className={style.blockTitle}>
        {t('Technical information')}
        <ButtonCopyDetails copyDetailsData={copyDetailsData} tooltipInfo={t('Copy Technical information.')} />
      </div>
      <div className={style.blockContainer}>
        <div className={style.extraInfo}>
          <SensorType type={hardware_type} partNumber={point?.sensor?.partNumber} />
          <InfoField
            label={t(isMote || isSensor ? 'Hex Code' : 'Serial Number')}
            value={sensorHEX || '-'}
            rightElement={
              sensorHEX?.length >= 6 ? (
                <CopyIcon className={style.copyIcon} onClick={() => copyHexSerial()} />
              ) : undefined
            }
          />
          <InfoField label={t('Asset code')} value={equipment?.assetCode || '-'} />
          {!isMote && <InfoField label={t('Node')} value={point?.sensor?.node?.serial || '-'} />}
          {profileData?.userAccess?.manageData && (
            <div className={style.replaceBtnWrapper}>
              <Button
                type="outline"
                variant={'small'}
                text={t('Replace Sensor')}
                onClick={() =>
                  setActiveModal({
                    name: MODAL_NAMES.sensorReplaceModal,
                    options: { instPoint: point },
                  })
                }
              />
            </div>
          )}
        </div>
        <div className={style.mainInfo}>
          <div className={style.infoFields}>
            <InfoField label={t('Installation point')} value={point?.name || '-'} />
            <InfoField label={t('Id')} value={point.id} />

            <InfoField label={t('Equipment')} value={equipment?.name || '-'} />
            <InfoField label={t('Id')} value={equipment?.id || '-'} />

            <InfoField label={t('Location')} value={locationInfo?.name || '-'} />
            <InfoField label={t('Id')} value={locationInfo?.id || '-'} />

            <InfoField label={t('Facility name')} value={locationInfo?.facilityName || '-'} />
            <InfoField label={t('Id')} value={locationInfo?.facilityId || '-'} />
          </div>
          <Link
            to={`/customer/${customerId}/sensors/${point.equipmentId}/list`}
            state={{ prevPage: pathname }}
            className={style.linkSensorsList}
          >
            {t('View sensors list')}
          </Link>
        </div>
        <div className={style.galleryBlock}>
          <Gallery
            images={imagesData}
            variant={'equipment'}
            editEnabled={profileData?.userAccess?.manageEquipmentDeactivation}
            onEditPhotos={handleOpenEdit}
          />
        </div>
      </div>
    </div>
  );
};

export default SensorInformation;
